import { computed, action, observable } from 'mobx';
import { StoreBase } from '../../../common/StoreBase';
import { IUserDetailConfiguration } from '../models';
import { TUpdateOwnUserRequestBody } from '@kurtosys/ksys-api-client/dist/models/requests/auth/TUpdateOwnUserRequestBody';
import { IRequestOptions } from '@kurtosys/ksys-api-client/dist/models/IRequestOptions';
import isEmailValid from '@kurtosys/ksys-app-template/dist/utils/isEmailValid';
import { ILabelledInputProps } from '@kurtosys/ksys-app-components/dist/components/base/LabelledInput/models';
import { utils } from '@kurtosys/ksys-app-template';

const { isNullOrEmpty } = utils.typeChecks;

export class UserDetailStore extends StoreBase {
	static componentKey: 'userDetail' = 'userDetail';
	@observable public name: string = '';
	@observable public email: string = '';

	@computed
	get configuration(): IUserDetailConfiguration | undefined {
		if (this.storeContext && this.storeContext.appStore) {
			return this.storeContext.appStore.getComponentConfiguration(UserDetailStore.componentKey);
		}
	}

	validateName = (value: string) => {
		if (isNullOrEmpty(value)) {
			return { isValid: false, message: this.nameRequiredErrorText };
		}
		return { isValid: true };
	}

	validateEmail = (value: string) => {
		if (isNullOrEmpty(value)) {
			return { isValid: false, message: this.emailRequiredErrorText };
		}
		if (!isEmailValid(value)) {
			return { isValid: false, message: this.emailValidationErrorText };
		}
		return { isValid: true };
	}

	public onSubmitRequestSaveUserDetail = async () => {
		const { messageStore } = this.storeContext;
		messageStore.clearAll(UserDetailStore.componentKey);

		const { appStore, kurtosysApiStore } = this.storeContext;
		const loadingKey = 'UserDetailStore.onSubmitRequestSaveUserDetail';
		appStore.startLoading(loadingKey);
		const overrides: Partial<IRequestOptions<TUpdateOwnUserRequestBody>> = {
			body: {
				name: this.name,
				email: this.email,
			},
		};
		const response = await kurtosysApiStore.updateOwnUser.callApi(overrides);
		if (response.status === 200) {
			messageStore.setInfoText(this.successMessage, UserDetailStore.componentKey);
		}
		else {
			messageStore.setErrorText(this.errorMessage, UserDetailStore.componentKey);
		}
		appStore.stopLoading(loadingKey);
	}

	@action
	async initialize(): Promise<void> {
		const { appStore } = this.storeContext;
		if (appStore.user && appStore.user.name) {
			this.name = appStore.user.name;
		}
		if (appStore.user && appStore.user.email) {
			this.email = appStore.user.email;
		}
	}

	@computed
	get hasValidInputs(): boolean {
		return !isNullOrEmpty(this.name) && !isNullOrEmpty(this.email) && isEmailValid(this.email);
	}

	@computed
	get nameLabelledInputProps(): ILabelledInputProps | any {
		return {
			labelProps: {
				value: this.userDetailNameLabelText,
			},
			inputProps: {
				value: this.name,
				onChange: this.onChangeName,
			},
			inputValidation: this.validateName,
		};
	}

	@computed
	get emailLabelledInputProps(): ILabelledInputProps | any {
		return {
			labelProps: {
				value: this.userDetailEmailLabelText,
			},
			inputProps: {
				value: this.email,
				onChange: this.onChangeEmail,
			},
			inputValidation: this.validateEmail,
		};
	}

	@action
	onChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.name = event.target.value;
	}

	@action
	onChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.email = event.target.value;
	}

	@computed
	get saveUserDetailButtonText(): string {
		return (this.configuration && this.configuration.saveUserDetailButtonText) ||
			'Save User Details';
	}

	@computed
	get successMessage(): string {
		return (this.configuration && this.configuration.successMessage) ||
			'Successfully updated user details';
	}

	@computed
	get errorMessage(): string {
		return (this.configuration && this.configuration.errorMessage) ||
			'Could not update user details';
	}

	@computed
	get nameRequiredErrorText(): string {
		return (this.configuration && this.configuration.nameRequiredErrorText) ||
			'Name is required.';
	}

	@computed
	get emailRequiredErrorText(): string {
		return (this.configuration && this.configuration.emailRequiredErrorText) ||
			'Email is required.';
	}

	@computed
	get emailValidationErrorText(): string {
		return (this.configuration && this.configuration.emailFormatErrorText) ||
			'Email is invalid.';
	}

	@computed
	get userDetailNameLabelText(): string {
		return (this.configuration && this.configuration.userDetailNameLabelText) ||
			'Name';
	}

	@computed
	get userDetailEmailLabelText(): string {
		return (this.configuration && this.configuration.userDetailEmailLabelText) ||
			'Email';
	}

}